<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h5 class="modal-title">{{ isEdit ? 'Edit' : 'New' }} Training Category</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body">
      <div class="setting-item">
        <label for="categoryName">Category Name <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
              <line x1="16" y1="8" x2="2" y2="22"></line>
              <line x1="17.5" y1="15" x2="9" y2="15"></line>
            </svg>
          </div>
          <input
            id="categoryName"
            v-model="form.name"
            type="text"
            class="form-control"
            :class="{ 'border-danger': v$.name.$error }"
            placeholder="e.g. Cyber Security, Compliance..."
            autofocus
            @blur="v$.name.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.name.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">Category name is required.</div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
      <button type="button" class="btn btn-primary" @click="handleSave">Save Category</button>
    </div>
  </vue-final-modal>
</template>

<script setup>
import { reactive, computed, watch, defineEmits, defineProps } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

const props = defineProps(['modelValue', 'isEdit', 'initialData']);
const emit = defineEmits(['update:modelValue', 'save']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const form = reactive({
  name: props.initialData?.name || ''
});

const rules = computed(() => ({
  name: { required }
}));

const v$ = useVuelidate(rules, form);

// Reset form and validation when modal opens
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    form.name = props.initialData?.name || '';
    v$.value.$reset();
  }
});

const closeModal = () => {
  isOpen.value = false;
};

const handleSave = async () => {
  const isFormValid = await v$.value.$validate();

  if (!isFormValid) return;

  emit('save', { ...form });
};
</script>

<style scoped>
/* Modal Overlay & Container */
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.modern-modal-content) {
  background: white;
  border-radius: 12px;
  width: 100%; /* Changed to 100% to fill the margin bounds */
  max-width: 480px; /* Slightly tighter max-width */
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden; /* Ensures inner backgrounds don't bleed past border-radius */
}

/* Tighter, Centered Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center; /* Changed from flex-start to center */
  padding: 1rem 1.5rem; /* Reduced from 1.5rem 2rem */
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff; /* Changed to white to match your image */
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem; /* Slightly smaller to prevent text wrap */
  font-weight: 700;
  letter-spacing: -0.01em;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

/* Tighter Body Padding */
.modal-body {
  padding: 1.25rem 1.5rem; /* Reduced padding to fix visual overflow */
  flex-grow: 1;
}

.setting-item label {
  display: block;
  font-size: 0.8rem; /* Slightly smaller label */
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
}

.form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

/* Tighter Footer Padding */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.5rem; /* Reduced from 1.25rem 2rem */
  gap: 0.75rem;
  background-color: #f8fafc;
}

.btn {
  padding: 0.5rem 1.25rem; /* Thinner buttons */
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.btn-primary {
  background-color: #00a0c1;
  color: white;
  border-color: #00a0c1;
}

.btn-primary:hover {
  background-color: #008da8;
  border-color: #008da8;
}

.btn-secondary {
  background-color: white;
  color: #64748b;
  border-color: #e2e8f0;
}

.btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}
</style>